import {
  createAction,
  createReducer,
} from '@reduxjs/toolkit'

import {
  Media,
  MediaType,
} from '@/model/media'

export const NEW_MESSAGE_KEY = 0 as const

const namespace = 'messageDraft'

export interface MessageDraftState {
  text: string
  medias: Media[]
  sensitiveWords: string[]
  version: number
  price?: number
  sendMediaType: MediaType
  cover?: string
}

export interface UpdateMessageDraftParams {
  creatorId: number
  fanUserId?: number
  keepVersion?: boolean
  changes: Omit<Partial<MessageDraftState>, 'version'>
}

export const updateMessageDraft = createAction<UpdateMessageDraftParams>(`${namespace}/updateMessageDraft`)

export interface UpdateMessageDraftMediaParams {
  creatorId: number
  fanUserId?: number
  media: Media
}

export const updateMessageDraftMedia = createAction<UpdateMessageDraftMediaParams>(`${namespace}/updateMessageDraftMedia`)

export interface AddMessageDraftMediaParams {
  creatorId: number
  fanUserId?: number
  media: Media
}

export const addMessageDraftMedia = createAction<AddMessageDraftMediaParams>(`${namespace}/addMessageDraftMedia`)

export interface ClearMessageDraftParams {
  creatorId: number
  fanUserId?: number
}

export const clearMessageDraft = createAction<ClearMessageDraftParams>(`${namespace}/clearMessageDraft`)

export const defaultMessageDraftState: MessageDraftState = {
  text: '',
  medias: [],
  sensitiveWords: [],
  version: 0,
  sendMediaType: MediaType.Image,
  price: undefined,
  cover: undefined,
}

type MessageDraftStateMap = Record<number, Record<number, MessageDraftState | undefined> | undefined>

const initialState: MessageDraftStateMap = {}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(
    updateMessageDraft,
    (draft, {
      payload: {
        creatorId,
        fanUserId = NEW_MESSAGE_KEY,
        changes,
        keepVersion = false,
      },
    }) => {
      const fanUserMap = draft[creatorId] || {}
      if (!draft[creatorId]) {
        draft[creatorId] = fanUserMap
      }
      const messageDraftState = fanUserMap[fanUserId]
      if (messageDraftState) {
        Object.assign(
          messageDraftState,
          changes,
          { version: keepVersion ? messageDraftState.version : messageDraftState.version += 1 },
        )
      } else {
        fanUserMap[fanUserId] = {
          ...defaultMessageDraftState,
          ...changes,
        }
      }
    },
  )
  builder.addCase(
    clearMessageDraft,
    (draft, { payload: { creatorId, fanUserId = NEW_MESSAGE_KEY } }) => {
      const fanUserMap = draft[creatorId]
      if (fanUserMap && fanUserMap[fanUserId]) {
        delete fanUserMap[fanUserId]
      }
    },
  )
  builder.addCase(
    updateMessageDraftMedia,
    (draft, { payload: { creatorId, fanUserId = NEW_MESSAGE_KEY, media } }) => {
      const messageDraftState = draft[creatorId]?.[fanUserId]
      if (messageDraftState) {
        const { medias } = messageDraftState
        const mediaIndex = medias.findIndex((item) => media.id === item.id)
        if (mediaIndex >= 0) {
          medias[mediaIndex] = media
        }
      }
    },
  )
  builder.addCase(
    addMessageDraftMedia,
    (draft, { payload: { creatorId, fanUserId = NEW_MESSAGE_KEY, media } }) => {
      const creatorMap = draft[creatorId] || {}
      if (!draft[creatorId]) {
        draft[creatorId] = creatorMap
      }
      const messageDraftState = creatorMap[fanUserId]
      if (messageDraftState) {
        const { medias } = messageDraftState
        medias.push(media)
      } else {
        creatorMap[fanUserId] = {
          ...defaultMessageDraftState,
          medias: [media],
        }
      }
    },
  )
})

export default { [namespace]: reducer }
